import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";

import { CAR_TYPES, DEFAULT_TONNAGE } from "../../constants/averageRates";
import useAverageRates from "../../hooks/useAverageRates";
import { hasStatisticsLicense, getLicenseDaysForButton, hasPayedServices } from "../../store/selectors";
import { buildAnalyticsUrl, buildBillingUrl, generateFakeRates, toPointParam } from "../../utils/averageRatesHelpers";
import { reachGoal, GOALS } from "../../utils/gtm";
import AverageRates from "./AverageRates";

const AverageRatesContainer = ({ routeRequest, hasLicense, payedServices, licenseDays, t }) => {
  const {
    carType,
    selectCarType,
    availableCarTypes,
    rates,
    isVisible,
    canFetchRates,
    fromCityId,
    toCityId,
  } = useAverageRates({ routeRequest, hasLicense });

  const handlePayClick = useCallback(() => {
    reachGoal(GOALS.TraceAvgprice, "AvgPrice");
    window.open(buildBillingUrl({ hasPayedServices: payedServices }), "_blank");
  }, [payedServices]);

  const carTypesForUi = useMemo(() => {
    const available = new Set(availableCarTypes || []);
    return CAR_TYPES.map(item => ({
      ...item,
      disabled: !available.has(item.value),
    }));
  }, [availableCarTypes]);

  const fakeRates = useMemo(() => generateFakeRates(), [fromCityId, toCityId]);

  // открытый блок — только после успешного last-rates; paywall — после similar
  if (!isVisible || !availableCarTypes) {
    return null;
  }

  if (canFetchRates && !rates) {
    return null;
  }

  const showOpen = canFetchRates;
  const displayRates = showOpen ? rates : fakeRates;

  return (
    <AverageRates
      variant={showOpen ? "rates" : "paywall"}
      averagePrice={displayRates.averagePrice}
      minPrice={displayRates.minPrice}
      maxPrice={displayRates.maxPrice}
      carType={carType}
      carTypes={carTypesForUi}
      tonnageLabel={t("trace-average-rates-tonnage-20", "20 т")}
      days={licenseDays}
      onCarTypeChange={selectCarType}
      onPayClick={handlePayClick}
      analyticsHref={buildAnalyticsUrl({
        fromPoint: toPointParam(fromCityId),
        toPoint: toPointParam(toCityId),
        carType,
        tonnage: DEFAULT_TONNAGE,
      })}
    />
  );
};

AverageRatesContainer.propTypes = {
  routeRequest: PropTypes.object,
  hasLicense: PropTypes.bool.isRequired,
  payedServices: PropTypes.bool.isRequired,
  licenseDays: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

AverageRatesContainer.defaultProps = {
  routeRequest: null,
};

const mapStateToProps = state => ({
  routeRequest: state.routeRequest,
  hasLicense: hasStatisticsLicense(state),
  payedServices: hasPayedServices(state),
  licenseDays: getLicenseDaysForButton(state),
});

export default connect(mapStateToProps)(withNamespaces()(AverageRatesContainer));
